<template>
  <div class="full_width">
    <v-btn class="refresh-button" @click="refreshOrders">
      <v-icon color="#6E6E6E">mdi-sync</v-icon>
    </v-btn>
    <div class="table-style">
      <div class="table-header">
        <div class="pill-radio-buttons-div">
          <MultiLinePillRadioButtons
            :value="statusSelection"
            :itemsList="statusValuesList"
            hasMultipleLines
            row-key="rowGroup"
            @input="statusSelection = $event"
          />
        </div>
        <div class="pill-radio-buttons-div">
          <v-text-field
            v-model="searchValue"
            rounded
            outlined
            dense
            full-width
            prepend-inner-icon="mdi-magnify"
            label="Suche"
            single-line
            hide-details
            @input="searchOrders"
          />
        </div>
      </div>

      <div v-if="serverError">
        <h1>{{ $t('order_details.loading_error') }}</h1>
        <p>{{ $t('order_details.error_hint') }}</p>
      </div>
      <v-data-table
        v-else
        disable-sort
        :headers="headers"
        :items="orders"
        item-key="orderId"
        :loading="loading"
        :loading-text="$t('overview_table.loading_text')"
        :no-data-text="
          isDataLoaded
            ? $t('overview_table.no_filter_data')
            : $t('overview_table.error_data')
        "
        :footer-props="{ 'items-per-page-options': [10] }"
        :server-items-length="orderCount"
        :options.sync="options"
        :page="currentPage"
        calculate-widths
      >
        <template v-slot:[`item.date`]="{ item }">
          <span v-if="item.date">
            {{ item.date | formatDate('DD.MM.YYYY HH:mm') }}
          </span>
          <span v-else>-</span>
        </template>
        <template v-slot:[`item.address`]="{ item }">
          <div>{{ item.address }}</div>
          <div v-if="item.additionalAddressCount" class="additional-addresses">
            {{
              $tc(
                'event_electricity.additional_addresses',
                item.additionalAddressCount,
                { count: item.additionalAddressCount }
              )
            }}
          </div>
        </template>
        <template v-slot:[`item.status`]="{ item }">
          <v-btn rounded dark :color="item.status.color">
            {{ item.status.label }}
          </v-btn>
        </template>
        <template v-slot:[`item.edit`]="{ item }">
          <v-btn icon @click="showDetails(item)">
            <v-icon>mdi-pencil-box-outline</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import { getCommissioningOrdersWithFilter } from '../../services/CommissioningService';
import { MultiLinePillRadioButtons } from '@newnetzportal/netzportal-webapp-storybook-components';
import { getCommissioningStatusValuesList } from '../../helpers/commissioningStatuses';
import { CONSTRUCTION_AND_EVENT_POWER_JOURNEY_TYPES } from '../../helpers/constructionAndEventPowerJourneys';
import { getCommissioningOrderById } from '../../services/CommissioningService';
import {
  needsSiteElectricityAddressDetails,
  mergeOrderAddressDetails,
  isEventElectricityOrder,
} from '../clerk-orders-overview/transform_raw_data_to_orders_table_format';
import { getConstructionAndEventPowerTableConfig } from './constructionAndEventPowerTableConfig';
import { constructionAndEventPowerTableOrderMapper } from './transformRawConstructionAndEventPowerOrder';

export default {
  name: 'ConstructionAndEventPowerOverviewTable',
  components: { MultiLinePillRadioButtons },
  props: {
    journeySelection: { type: String, default: 'all' },
  },
  data() {
    return {
      currentPage: this.$route.query.page
        ? parseInt(this.$route.query.page, 10)
        : 1,
      searchValue: this.$route.query.search || '',
      statusSelection: (this.$route.query.order_status || '').startsWith(
        'WMSB_'
      )
        ? 'all'
        : this.$route.query.order_status || 'all',
      statusValuesList: getCommissioningStatusValuesList().filter(
        (status) => status.rowGroup !== 'wmsb'
      ),
      orders: [],
      orderCount: 0,
      options: {},
      loading: false,
      isDataLoaded: false,
      serverError: false,
      headers: getConstructionAndEventPowerTableConfig('headers'),
    };
  },
  watch: {
    journeySelection(value) {
      this.currentPage = 1;
      this.updateURLParam('journey_type', value === 'all' ? '' : value);
      this.$emit('journey-change', value);
      this.loadOrders();
    },
    statusSelection(value) {
      this.currentPage = 1;
      this.updateURLParam('order_status', value === 'all' ? '' : value);
      this.loadOrders();
    },
    searchValue(value) {
      this.updateURLParam('search', value);
    },
    options: {
      deep: true,
      handler(value) {
        this.currentPage = value.page;
        this.updateURLParam('page', value.page);
        this.loadOrders();
      },
    },
  },
  created() {
    if ((this.$route.query.order_status || '').startsWith('WMSB_')) {
      this.updateURLParam('order_status', '');
    }
    this.$emit('journey-change', this.journeySelection);
    this.loadOrders();
  },
  methods: {
    updateURLParam(name, value) {
      const params = this.$route.query;
      if (!value) delete params[name];
      else params[name] = value;
      history.replaceState(
        {},
        null,
        `${this.$route.path}?${new URLSearchParams(params)}`
      );
    },
    refreshOrders() {
      this.searchValue = '';
      this.statusSelection = 'all';
      this.currentPage = 1;
      Object.keys(this.$route.query).forEach(
        (key) => delete this.$route.query[key]
      );
      this.updateURLParam('page', 1);
      this.$emit('journey-change', 'all');
      if (this.journeySelection === 'all') this.loadOrders();
    },
    searchOrders(value) {
      if (value.length === 0 || value.length > 2) {
        this.currentPage = 1;
        this.loadOrders();
      }
    },
    showDetails(order) {
      this.$router.push({
        name:
          order.journeyType === 'SITE_ELECTRIC_METER_ORDER'
            ? 'CommissioningDetailsPage'
            : 'EventElectricityDetailsPage',
        params: { id: order.orderId },
      });
    },
    async loadOrders() {
      this.loading = true;
      try {
        const response = await getCommissioningOrdersWithFilter({
          limit: 10,
          from_index: (this.currentPage - 1) * 10,
          journey_types: (this.journeySelection &&
          this.journeySelection !== 'all'
            ? [this.journeySelection]
            : CONSTRUCTION_AND_EVENT_POWER_JOURNEY_TYPES
          ).join(','),
          order_status:
            this.statusSelection === 'all' ? '' : this.statusSelection,
          search_word: this.searchValue,
        });

        if (!response) {
          throw new Error('No construction and event power orders received.');
        }

        this.orderCount = response.count_orders || 0;
        const orders = await Promise.all(
          (response.items || []).map(async (order) => {
            if (
              !isEventElectricityOrder(order) &&
              needsSiteElectricityAddressDetails(order)
            ) {
              return mergeOrderAddressDetails(
                order,
                await getCommissioningOrderById(order.id)
              );
            }
            return order;
          })
        );
        this.orders = orders.map(constructionAndEventPowerTableOrderMapper);
        this.serverError = false;
        this.isDataLoaded = true;
      } catch (error) {
        console.error(
          'Error fetching construction and event power orders:',
          error
        );
        this.orders = [];
        this.orderCount = 0;
        this.serverError = true;
        this.isDataLoaded = false;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.additional-addresses {
  color: #6e6e6e;
  font-size: 0.75rem;
}
</style>
